export type CompatibilityReason =
  | "ok"
  | "insecure-context"
  | "no-getusermedia"
  | "in-app-browser"
  | "mic-denied";

export interface CompatibilityResult {
  ok: boolean;
  reason: CompatibilityReason;
}

const IN_APP_SIGNATURES = [
  "FBAN", "FBAV", "FB_IAB",
  "Instagram",
  "Twitter",
  "Line/",
  "MicroMessenger",
  "KAKAOTALK",
  "TikTok",
  "LinkedInApp",
  "Snapchat",
];

export function isInAppBrowser(ua: string = navigator.userAgent): boolean {
  return IN_APP_SIGNATURES.some((sig) => ua.indexOf(sig) !== -1);
}

export function hasWebRTCSupport(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function"
  );
}

export async function checkVoiceCompatibility(): Promise<CompatibilityResult> {
  if (typeof window === "undefined") {
    return { ok: false, reason: "no-getusermedia" };
  }

  if (window.isSecureContext === false) {
    return { ok: false, reason: "insecure-context" };
  }

  if (isInAppBrowser()) {
    return { ok: false, reason: "in-app-browser" };
  }

  if (!hasWebRTCSupport()) {
    return { ok: false, reason: "no-getusermedia" };
  }

  try {
    const perms = (navigator as any).permissions;
    if (perms && typeof perms.query === "function") {
      const status = await perms.query({ name: "microphone" as PermissionName });
      if (status.state === "denied") {
        return { ok: false, reason: "mic-denied" };
      }
    }
  } catch {
    // Permissions API rejects `microphone` on Safari and older Firefox.
    // We fall through to ok; runtime getUserMedia failure is caught later
    // and translated via translateRuntimeError — this is the safety net.
  }

  return { ok: true, reason: "ok" };
}
